import * as React from 'react';
import { ICompetition } from './interfaces';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { ROUTES } from '@routes/routes';
import { Image } from '@components/Image';
import {
  getCompetitionStatusName,
  getCompetitionFormatName,
} from '../../../helpers';
import { CompetitionDetailsList } from '../../../components/CompetitionDetailsList';
import { useTranslation } from 'react-i18next';
import { WeaponGroupChips } from '@components/competitions/WeaponGroupChips';

export const Competition: React.FC<ICompetition> = ({
  slug,
  title,
  format,
  discipline,
  weaponGroups,
  status,
  description,
  enrollmentCount,
  enrolled,
  imgSrc,
  startDate,
  endDate,
  detailsRoute,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formatName = getCompetitionFormatName(format);
  const statusName = getCompetitionStatusName(status);

  const onCompetitionClick = (): void => {
    navigate(
      detailsRoute ?? ROUTES.portal.competitions.details(slug)
    );
  };

  return (
    <Card
      sx={
        enrolled
          ? {
              border: '2px solid',
              borderColor: 'success.main',
            }
          : undefined
      }
    >
      <CardActionArea onClick={onCompetitionClick}>
        <Box sx={{ position: 'relative' }}>
          <Image
            imageSrc={imgSrc}
            aspectRatio={4 / 2}
            sx={{
              mt: 0,
              border: 'none',
              borderRadius: 0,
            }}
          />
          {enrolled && (
            <Chip
              label={t('competitions:portal.overview.enrolledBadge')}
              size="small"
              color="success"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 1,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
          )}
        </Box>
        <CardContent>
          <Typography
            variant="h5"
            component="div"
            fontSize="1rem"
            fontWeight="bold"
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            fontSize=".8rem"
            color="text.secondary"
          >
            {discipline}
          </Typography>
          <Box sx={{ mb: '.8em', mt: 0.5 }}>
            <WeaponGroupChips weaponGroups={weaponGroups} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>

          <Divider
            sx={{
              my: '10px',
            }}
          />
          <CompetitionDetailsList
            startDate={startDate}
            endDate={endDate}
            statusName={statusName}
            participants={enrollmentCount}
            formatName={formatName}
          />
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
